import { normalizeUserRoleKey, USER_ROLES } from './AuthContext';

const PARTNER_ROLES = new Set([
  'vendor',
  'usedvendor',
  'usedpending',
  'garage',
  'mechanics',
  'shipping',
  'delivery',
  'superadmin',
  'admin',
]);

/** Phone / social login users without a partner role are treated as customers (Blinkit-style). */
export function resolveLoginRole(role) {
  const normalized = normalizeUserRoleKey(role);
  if (!normalized || normalized === 'pending') return USER_ROLES.CUSTOMER;
  return normalized;
}

export function isPartnerRole(role) {
  return PARTNER_ROLES.has(normalizeUserRoleKey(role));
}

/** Login page for partner dashboards (vendor, garage, mechanics, shipping, admin). */
export function getPartnerLoginPath() {
  return '/login/partner';
}

/** Pick customer vs partner login based on protected route. */
export function getLoginPathForRoute(pathname = '') {
  if (
    pathname.startsWith('/vendor/') ||
    pathname.startsWith('/garage/') ||
    pathname.startsWith('/mechanics/') ||
    pathname.startsWith('/shipping/') ||
    pathname.startsWith('/admin/')
  ) {
    return getPartnerLoginPath();
  }
  return '/login';
}

export function getPartnerOnboardingPath(role) {
  const r = normalizeUserRoleKey(role);
  const paths = {
    customer: '/onboarding/customer',
    vendor: '/onboarding/vendor',
    garage: '/onboarding/garage',
    mechanics: '/onboarding/mechanics',
    shipping: '/onboarding/delivery',
    delivery: '/onboarding/delivery',
    usedpending: '/signup/pending',
  };
  return paths[r] || '/';
}

export function getDashboardPathForRole(role) {
  const normalized = normalizeUserRoleKey(role);
  const paths = {
    customer: '/',
    vendor: '/vendor/dashboard',
    usedvendor: '/vendor/dashboard',
    used_vendor: '/vendor/dashboard',
    usedpending: '/signup/pending',
    used_pending: '/signup/pending',
    mechanics: '/mechanics/dashboard',
    mechanic: '/mechanics/dashboard',
    superadmin: '/admin/dashboard',
    admin: '/admin/dashboard',
    garage: '/garage/dashboard',
    shipping: '/shipping/dashboard',
    delivery: '/shipping/dashboard',
  };
  return paths[normalized] || '/';
}

/**
 * Where to send the user after login.
 * Non-partner or unknown roles → home (customer). Partners → dashboard or onboarding.
 */
/**
 * True only for a path that stays on this site.
 *
 * `from` is captured from `location.pathname` when a guard bounces a visitor to
 * login, so its value is whatever URL they arrived on — which an attacker picks
 * by choosing the link they send. Passing that straight to navigate() is an open
 * redirect: react-router 6.30.4 resolves a leading `/\` or `//` as
 * protocol-relative (GHSA-wrjc-x8rr-h8h6), so
 *
 *     https://sparepe.com/\evil.example
 *
 * routes through our login and lands the visitor on evil.example — having just
 * entered their credentials on a page whose domain they checked. That is the
 * whole value of the trick.
 *
 * Fixed here rather than by upgrading the router: the upstream fix exists only
 * in v7, and 211 files import react-router (458 <Link>, 267 useNavigate) with 33
 * tests covering them. Validating the single attacker-controlled value is the
 * proportionate change. The upgrade is still worth scheduling separately.
 *
 * Requires exactly one leading slash: rejects `//host`, `/\host` and absolute
 * URLs, while leaving ordinary in-app paths alone.
 */
export function isSafeInternalPath(pathname) {
  return (
    typeof pathname === 'string' &&
    pathname.startsWith('/') &&
    !pathname.startsWith('//') &&
    !pathname.startsWith('/\\')
  );
}

export function getPostLoginPath(user, { from } = {}) {
  const role = normalizeUserRoleKey(user?.role);
  // Anything not provably internal is dropped, so the fallbacks below apply
  // exactly as they would for someone who arrived at login directly.
  const safeFrom = isSafeInternalPath(from?.pathname) ? from : undefined;

  if (role === 'superadmin' || role === 'admin') {
    return '/admin/dashboard';
  }

  if (!isPartnerRole(role)) {
    if (safeFrom?.pathname && !safeFrom.pathname.startsWith('/onboarding')) {
      return { pathname: safeFrom.pathname, search: safeFrom.search || '', state: safeFrom.state };
    }
    return '/';
  }

  if (user?.onboardingComplete === false) {
    return getPartnerOnboardingPath(role);
  }

  if (safeFrom?.pathname) {
    return { pathname: safeFrom.pathname, search: safeFrom.search || '', state: safeFrom.state };
  }

  return getDashboardPathForRole(role);
}
